/**
 * What the summary email and the portal say about each vault's Scheduled
 * check (ADR 0028; **Proven readable**, **Rolling verify** and **Scheduled
 * check** in the desktop glossary): its one line, why it needs attention,
 * and the stored size its checks measured. One module, so the two screens
 * say the same words about the same vault.
 *
 * The line never says "verified", "tested" or "restorable", and it never
 * calls an Archive vault proven readable: a Deep check there says the files
 * are in the archive and can only be read back after a retrieval.
 */

import { formatDollars } from "./hyperscalerPricing";
import {
  defaultRollingVerifyCycleDays,
  type RollingVerifyTier,
  rollingVerifyMonthlyCost,
} from "./rollingVerifyPricing";
import { readVaultStorageTier } from "./storageTier";
import type { PortalOverview, Repository, VaultCheck } from "./types";

/**
 * The Stored figure: each vault's size as the last scheduled check that
 * listed it measured, once per vault however many devices share it. A row
 * that carries a size of its own and belongs to no measured vault still
 * counts. Null, never zero, when nothing has measured one: a zero reads as
 * "100% saved by deduplication", which is worse than an empty tile because
 * it reads as an answer.
 */
export function storedBytesOf(overview: Pick<PortalOverview, "repositories" | "vaults">): number | null {
  const measured = (overview.vaults ?? []).filter((vault) => vault.storedBytes != null);
  const covered = new Set(measured.flatMap((vault) => vault.repositoryIds));
  const sizes = [
    ...measured.map((vault) => vault.storedBytes),
    ...overview.repositories.filter((repo) => !covered.has(repo.id)).map((repo) => repo.storedBytes),
  ].filter((size): size is number => typeof size === "number" && Number.isFinite(size));
  return sizes.length === 0 ? null : sizes.reduce((sum, size) => sum + size, 0);
}

/**
 * The vault check an overview reports for the vault behind these rows, if
 * any device has reported one.
 */
export function vaultCheckFor(
  vaults: readonly VaultCheck[] | undefined,
  repositoryIds: Iterable<string>,
): VaultCheck | undefined {
  const ids = new Set(repositoryIds);
  return vaults?.find((vault) => vault.repositoryIds.some((id) => ids.has(id)));
}

/**
 * What a Rolling verify of a vault would be priced against, from the portal
 * rows that report it: the storage tier of the first row that reported a
 * provider, or the destination kind of a vault with no storage tier (a
 * folder, a NAS, a sign-in cloud).
 *
 * Null for a bucket none of whose rows reported a provider, from a desktop
 * too old to say: its tier is not known, so it is neither free nor priced,
 * and never mistaken for a folder.
 */
export function rollingVerifyTierOf(
  rows: readonly Pick<Repository, "kind" | "provider" | "storageClass">[],
): RollingVerifyTier | null {
  const reporting = rows.find((row) => row.provider != null);
  if (reporting) return readVaultStorageTier(reporting.provider, reporting.storageClass);
  const kind = rows[0]?.kind;
  return kind == null || kind === "s3" ? null : kind;
}

/**
 * The cycle an Off vault's price is quoted at: about the stored size read
 * back once a month, the cycle a free vault starts at.
 */
const QUOTED_CYCLE_DAYS = 30;

/**
 * The line for a vault's Scheduled check.
 *
 * `tier` is `rollingVerifyTierOf` the vault's rows, and only matters at Off,
 * whose line suggests a Rolling verify where it is free and prices it where
 * it is billed. `timeZone` is the zone its dates are written in: the
 * account's for the summary, which passes `"UTC"` for an account that has
 * none, or `undefined` for the runtime's own, which in the portal is the
 * reader's.
 */
export function vaultCheckLine(
  vault: VaultCheck,
  tier: RollingVerifyTier | null,
  nowUtc: number,
  timeZone: string | undefined,
): string {
  const date = (utc: number) => checkDate(utc, nowUtc, timeZone);

  if (vault.scheduledCheck === "quick" || vault.scheduledCheck === "deep") {
    const name = CHECK_NAMES[vault.scheduledCheck];
    if (vault.lastCheckUtc == null) return `${name} hasn't run yet.`;
    if (vault.lastCheckFoundProblems === true) return `${name} on ${date(vault.lastCheckUtc)} found problems.`;
    // A Deep check is the scheduled check of an Archive vault and only of
    // one (ADR 0028), where reading contents back needs a paid retrieval.
    const reach =
      vault.scheduledCheck === "quick"
        ? "Files are there; their contents haven't been read back."
        : "File contents are in the archive and can only be read back after a retrieval.";
    return `${name} passed on ${date(vault.lastCheckUtc)}. ${reach}`;
  }

  if (vault.scheduledCheck === "off") {
    const hint = rollingVerifyHint(tier, vault.storedBytes);
    return hint == null ? "Not checked." : `Not checked. ${hint}`;
  }

  if (vault.provenReadableUtc != null) return `Proven readable on ${date(vault.provenReadableUtc)}.`;
  if (vault.readBytes != null && vault.neededBytes != null && vault.neededBytes > 0) {
    // Rounded down, so a pass is never said to be further on than it is.
    const share = Math.floor((Math.min(vault.readBytes, vault.neededBytes) / vault.neededBytes) * 100);
    return `Not yet proven readable: ${share}% read back so far.`;
  }
  return "Not yet proven readable.";
}

const DAY = 86_400;

/**
 * Whether a vault's Proven readable date has fallen more than two Rolling
 * verify cycles behind (ADR 0028), the rule the `unproven` alert sends on.
 * Only a vault some device runs a Rolling verify on can stall: one not yet
 * proven readable has no date to fall behind, and an Archive vault never has
 * one.
 */
export function vaultHasStalled(vault: VaultCheck, nowUtc: number): boolean {
  return (
    vault.scheduledCheck === "rolling" &&
    vault.cycleDays != null &&
    vault.provenReadableUtc != null &&
    nowUtc - vault.provenReadableUtc > 2 * vault.cycleDays * DAY
  );
}

/**
 * Why a vault needs attention, or null when it does not: its last scheduled
 * check found problems, or its Proven readable date has stalled
 * (`vaultHasStalled`), or both, problems first. A problem flag left from
 * before the check was turned off is no longer anyone's check, and a check
 * with no time to name it by is not counted, as the `vault-problem` alert
 * counts neither.
 */
export function vaultAttentionReason(vault: VaultCheck, nowUtc: number, timeZone: string | undefined): string | null {
  const reasons = [vaultProblemReason(vault, nowUtc, timeZone), vaultStallReason(vault, nowUtc, timeZone)].filter(
    (reason): reason is string => reason != null,
  );
  return reasons.length === 0 ? null : reasons.join(" ");
}

/**
 * The `vault-problem` half of `vaultAttentionReason`: "The Rolling verify on
 * 3 October found problems.", or null when the vault's last scheduled check
 * found none, has no time to name it by, or the vault is at Off.
 */
export function vaultProblemReason(vault: VaultCheck, nowUtc: number, timeZone: string | undefined): string | null {
  if (vault.lastCheckFoundProblems !== true || vault.lastCheckUtc == null || vault.scheduledCheck === "off") {
    return null;
  }
  return `The ${CHECK_NAMES[vault.scheduledCheck]} on ${checkDate(vault.lastCheckUtc, nowUtc, timeZone)} found problems.`;
}

/**
 * The `unproven` half of `vaultAttentionReason`, by `vaultHasStalled`, or
 * null when the vault has not stalled.
 */
export function vaultStallReason(vault: VaultCheck, nowUtc: number, timeZone: string | undefined): string | null {
  if (!vaultHasStalled(vault, nowUtc)) return null;
  return (
    `Proven readable on ${checkDate(vault.provenReadableUtc!, nowUtc, timeZone)}, more than two ` +
    `${vault.cycleDays}-day cycles ago: its Rolling verify has fallen behind.`
  );
}

/** What each Scheduled check that checks something is called. */
export const CHECK_NAMES: Record<Exclude<VaultCheck["scheduledCheck"], "off">, string> = {
  quick: "Quick check",
  deep: "Deep check",
  rolling: "Rolling verify",
};

/**
 * What an Off vault's line adds about a Rolling verify, or null for nothing:
 *
 * - where reading back is free for the tier (its default cycle is on), to
 *   turn one on;
 * - where it is billed and the stored size is known, what it would cost a
 *   month at a 30-day cycle;
 * - nothing for a billed tier of unknown size, a provider the app has no
 *   prices for, an Archive vault's tier (where it is never offered), or a
 *   tier that is not known at all.
 */
function rollingVerifyHint(tier: RollingVerifyTier | null, storedBytes: number | undefined): string | null {
  if (tier == null) return null;
  if (defaultRollingVerifyCycleDays(tier) != null) return "Turn on Rolling verify on the Vault screen.";
  if (storedBytes == null) return null;
  const cost = rollingVerifyMonthlyCost(tier, storedBytes, QUOTED_CYCLE_DAYS);
  if (cost.kind !== "priced") return null;
  const amount = cost.dollarsPerMonth < 0.01 ? "less than $0.01" : `about ${formatDollars(roundToCents(cost.dollarsPerMonth))}`;
  return `Rolling verify would cost ${amount} a month.`;
}

function roundToCents(dollars: number): number {
  return Math.round(dollars * 100) / 100;
}

/**
 * "3 October", or "3 October 2025" for a date outside the current year, in
 * `timeZone`. A zone the runtime does not know is read as UTC rather than
 * thrown, as the summary's period arithmetic reads one.
 */
export function checkDate(utc: number, nowUtc: number, timeZone: string | undefined): string {
  const year = (at: number) => formatted(at, { year: "numeric" }, timeZone);
  const withYear = year(utc) !== year(nowUtc);
  return formatted(utc, { day: "numeric", month: "long", ...(withYear ? { year: "numeric" } : {}) }, timeZone);
}

function formatted(utc: number, options: Intl.DateTimeFormatOptions, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone }).format(utc * 1000);
  } catch {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" }).format(utc * 1000);
  }
}
